import React, { useState } from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import { Layout, Menu, Avatar, Dropdown, Typography, Space, Button, Tag, Divider } from 'antd'
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  DashboardOutlined,
  BarcodeOutlined,
  InboxOutlined,
  UserOutlined,
  LogoutOutlined,
  SettingOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons'
import { useSelector, useDispatch } from 'react-redux'
import { RootState } from '../../store'
import { logout } from '../../store/slices/authSlice'

const { Header, Sider, Content } = Layout
const { Text } = Typography

const MainLayout: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()

  const { user } = useSelector((state: RootState) => state.auth)

  // 菜单项配置
  const menuItems = [
    {
      key: '/',
      icon: <DashboardOutlined />,
      label: '仪表板',
    },
    {
      key: '/sku',
      icon: <BarcodeOutlined />,
      label: 'SKU管理',
    },
    {
      key: '/packing',
      icon: <InboxOutlined />,
      label: '装箱管理',
    },
    {
      key: '/users',
      icon: <UserOutlined />,
      label: '用户管理',
      // 只有管理员可以看到用户管理
      visible: user?.role === 'ADMIN',
    },
  ].filter(item => item.visible !== false)

  // 用户下拉菜单
  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: '个人资料',
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: '系统设置',
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      danger: true,
    },
  ]

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(key)
  }

  const handleUserMenuClick = ({ key }: { key: string }) => {
    switch (key) {
      case 'logout':
        dispatch(logout())
        navigate('/login')
        break
      case 'profile':
        // TODO: 实现个人资料页面
        break
      case 'settings':
        // TODO: 实现系统设置页面
        break
    }
  }

  const getRoleName = (role: string) => {
    const roleMap = {
      ADMIN: '管理员',
      BUSINESS: '业务员',
      PACKER: '装箱员',
      admin: '管理员',
      business: '业务员',
      packer: '装箱员',
    }
    return roleMap[role as keyof typeof roleMap] || role
  }

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        theme="light"
        style={{
          boxShadow: '2px 0 6px rgba(0,21,41,.35)',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          height: '100vh',
          zIndex: 1000,
          overflow: 'hidden',
        }}
      >
        <div style={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}>
          {/* 顶部标题 */}
          <div style={{
            height: '64px',
            padding: '16px',
            borderBottom: '1px solid #f0f0f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            <Text strong style={{ fontSize: collapsed ? '14px' : '16px' }}>
              {collapsed ? '装箱' : '扫码装箱系统'}
            </Text>
          </div>

          {/* 菜单区域 - 可滚动 */}
          <div style={{
            flex: 1,
            overflow: 'auto',
            minHeight: 0
          }}>
            <Menu
              mode="inline"
              selectedKeys={[location.pathname]}
              items={menuItems}
              onClick={handleMenuClick}
              style={{
                borderRight: 0,
                height: '100%'
              }}
            />
          </div>

          {/* 系统状态 - 固定在底部 */}
          <div style={{
            padding: '16px',
            borderTop: '1px solid #f0f0f0',
            background: '#fafafa',
            flexShrink: 0
          }}>
            {!collapsed && (
              <>
                <Text strong style={{ fontSize: '12px', color: '#666' }}>系统状态</Text>
                <Divider style={{ margin: '8px 0' }} />
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text style={{ fontSize: '11px' }}>数据库:</Text>
                    <Tag color="green" style={{ fontSize: '10px', margin: 0 }}>正常</Tag>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text style={{ fontSize: '11px' }}>在线用户:</Text>
                    <Text style={{ fontSize: '11px' }}>3人</Text>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text style={{ fontSize: '11px' }}>版本:</Text>
                    <Text style={{ fontSize: '11px' }}>v1.0.0</Text>
                  </div>
                </Space>
              </>
            )}
            {collapsed && (
              <div style={{ textAlign: 'center' }}>
                <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '16px' }} />
              </div>
            )}
          </div>
        </div>
      </Sider>

      <Layout style={{
        marginLeft: collapsed ? 80 : 200,
        transition: 'margin-left 0.2s'
      }}>
        <Header style={{
          padding: '0 24px',
          background: '#fff',
          borderBottom: '1px solid #f0f0f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'fixed',
          top: 0,
          right: 0,
          left: collapsed ? 80 : 200,
          zIndex: 999,
          transition: 'left 0.2s'
        }}>
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            style={{ fontSize: '16px', width: 64, height: 64 }}
          />

          <Space>
            <Text type="secondary">欢迎回来，</Text>
            <Dropdown
              menu={{
                items: userMenuItems,
                onClick: handleUserMenuClick,
              }}
              placement="bottomRight"
            >
              <Space style={{ cursor: 'pointer' }}>
                <Avatar size="small" icon={<UserOutlined />} />
                <Text strong>{user?.username}</Text>
                <Text type="secondary">({getRoleName(user?.role || '')})</Text>
              </Space>
            </Dropdown>
          </Space>
        </Header>

        <Content style={{
          margin: '88px 24px 24px 24px',
          padding: '24px',
          background: '#fff',
          borderRadius: '6px',
          minHeight: 'calc(100vh - 112px)',
          overflow: 'auto'
        }}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  )
}

export default MainLayout